import {
  Condo,
  PortfolioOverview,
} from '../../types';
import { apiFetch as rawApiFetch, apiJson, parseProblemDetail } from '../httpClient';
import type { ApiFetchOptions } from '../httpClient';

/**
 * Dev-only guard around the shared apiFetch helper.
 *
 * apiFetch (httpClient.ts) ALREADY prepends the `/api` base to the endpoint
 * path, so a caller that passes a path starting with `/api/` produces a
 * broken `/api/api/...` request → 404. This wrapper warns (in dev only) when
 * that mistake is made, catching the bug class at its source. The file's
 * convention is to pass paths WITHOUT the leading `/api`
 * (e.g. `/agm-packets/...`). Production behavior is unchanged — the guard is
 * compiled out of hot paths by the NODE_ENV check.
 */
export function apiFetch(endpoint: string, options: ApiFetchOptions = {}): Promise<Response> {
  if (process.env.NODE_ENV !== 'production' && endpoint.startsWith('/api/')) {
    // eslint-disable-next-line no-console
    console.warn(
      `apiFetch: endpoint should NOT start with /api (the base already adds it): ${endpoint}`,
    );
  }
  return rawApiFetch(endpoint, options);
}

export class ApiBase {
  // Delegated to httpClient (Sprint 2): timeout, X-Request-Id, 401 →
  // single-flight refresh-and-retry, hard-logout on refresh failure. Every
  // call site stays unchanged.
  protected request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return apiJson<T>(endpoint, options);
  }

  protected unauthenticatedRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return apiJson<T>(endpoint, { ...options, skipAuth: true });
  }

  // Auth endpoints
  async login(email: string, password: string) {
    // Backend now returns { token, refreshToken, user } — keep the shape
    // open so we can pick up the refresh token in authStore.
    return this.unauthenticatedRequest<{ token: string; refreshToken?: string; user: any }>(
      '/auth/login',
      { method: 'POST', body: JSON.stringify({ email, password }) },
    );
  }

  async register(userData: any) {
    return this.request<{ token: string; user: any }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  // ── Sprint 124 (ADR-117) — account activation / password reset ────────────

  /**
   * Empty-body POST helper. The backend activation endpoints answer 200 with an
   * EMPTY body (ResponseEntity.ok().build()), so we must NOT call response.json()
   * (that throws on an empty body). We surface a ProblemDetail-style error on a
   * non-2xx exactly like apiJson does, but skip body parsing on success.
   */
  protected async emptyBodyPost(endpoint: string, body: any, skipAuth = false): Promise<void> {
    const res = await apiFetch(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
      ...(skipAuth ? { skipAuth: true } : {}),
    } as ApiFetchOptions);
    if (!res.ok) {
      const errBody = await res.json().catch(() => null);
      const { message } = parseProblemDetail(res.status, errBody);
      const requestId = res.headers.get('X-Request-Id');
      const tail = requestId ? ` (id: ${requestId})` : '';
      throw new Error(message + tail);
    }
  }

  /**
   * Request an activation / password-reset link. Unauthenticated & public.
   * Always resolves whether or not the email exists — the backend never
   * enumerates. Doubles as forgot-password.
   */
  async requestActivation(email: string): Promise<void> {
    return this.emptyBodyPost('/auth/request-activation', { email }, true);
  }

  /**
   * Consume a raw activation token and set the account password.
   * Unauthenticated & public. A 400 is surfaced as an Error so the caller can
   * offer a "request a new link" action.
   */
  async setPassword(token: string, newPassword: string): Promise<void> {
    return this.emptyBodyPost('/auth/set-password', { token, newPassword }, true);
  }

  /**
   * Admin action: resend the activation invite for an owner whose login is
   * still unclaimed. `userId` is the owner's canonical key (Owner.userId).
   */
  async resendOwnerInvite(userId: string): Promise<void> {
    return this.emptyBodyPost(`/owners/${userId}/resend-invite`, undefined, false);
  }

  // ── Sprint 133.1 — public status-certificate request ──────────────────────

  /**
   * Public, unauthenticated. An external requestor (typically a buyer's
   * solicitor or prospective purchaser) requests a status certificate for a
   * unit without a login. The backend ALWAYS answers 202 with a uniform
   * envelope and never leaks whether the condo/unit exists, so we skip body
   * parsing on success and surface a generic error otherwise (ProblemDetail /
   * X-Request-Id handling preserved via emptyBodyPost).
   */
  async createPublicStatusCertRequest(payload: {
    condoId: string;
    requestorName: string;
    requestorEmail?: string;
    unitNumber: string;
    purpose?: string;
  }): Promise<any> {
    return this.emptyBodyPost('/public/status-certificate-requests', payload, true);
  }

  async getCurrentUser() {
    // Backend exposes GET /api/auth/user (see AuthController.java).
    // Calling /auth/me used to 404 silently and wipe the authToken,
    // leaving ProtectedRoute stuck on "Verifying session...".
    return this.request<any>('/auth/user');
  }

  // Condos endpoints
  async getCondos(): Promise<Condo[]> {
    return this.request<Condo[]>('/condos/available');
  }

  async getCondo(id: string): Promise<Condo> {
    return this.request<Condo>(`/condos/${id}`);
  }

  // Sprint 115 — platform/portfolio overview for the super-admin home.
  // Server resolves the caller's own authorised condos (same scope as
  // /condos/available) and returns REAL roll-ups. Metrics with no data
  // source (MRR, uptime, incidents) are intentionally NOT in this payload.
  async getPortfolioOverview(): Promise<PortfolioOverview> {
    return this.request<PortfolioOverview>('/portfolio/overview');
  }

  async createCondo(condo: Omit<Condo, 'id' | 'createdAt' | 'updatedAt'>): Promise<Condo> {
    return this.request<Condo>('/condos', {
      method: 'POST',
      body: JSON.stringify(condo),
    });
  }

  async updateCondo(id: string, condo: Partial<Condo>): Promise<Condo> {
    return this.request<Condo>(`/condos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(condo),
    });
  }

  async deleteCondo(id: string): Promise<void> {
    return this.request<void>(`/condos/${id}`, {
      method: 'DELETE',
    });
  }

  // ── Shared list helper (Sprint 94.5) ──────────────────────────────────────

  /**
   * Sprint 94.5 helper — GET a list endpoint where a missing resource is a
   * legitimately-empty state, but auth/permission failures must surface.
   *
   * Contract (matches the rest of this file: read res.status off apiFetch):
   *   • 401 / 403 → throw (caller/UI shows access-denied; auth errors never
   *     masquerade as "no data"). 401 still rides apiFetch's silent-refresh
   *     path first, so this only throws once the user is genuinely unauthorized.
   *   • 404      → [] (endpoint/resource not present → genuinely empty)
   *   • 2xx      → parsed body (or [] for 204 / empty body)
   *   • other !ok → throw with the request-id tail, same as apiJson.
   */
  protected async fetchListGraceful<T>(endpoint: string, options: RequestInit = {}): Promise<T[]> {
    const res = await apiFetch(endpoint, options);
    if (res.status === 401 || res.status === 403) {
      const body = await res.json().catch(() => ({} as any));
      const requestId = res.headers.get('X-Request-Id');
      const tail = requestId ? ` (id: ${requestId})` : '';
      throw new Error((body.detail || body.message || body.title || `HTTP ${res.status}`) + tail);
    }
    if (res.status === 404) return [];
    if (!res.ok) {
      const body = await res.json().catch(() => ({} as any));
      const requestId = res.headers.get('X-Request-Id');
      const tail = requestId ? ` (id: ${requestId})` : '';
      throw new Error((body.detail || body.message || body.title || `HTTP ${res.status}`) + tail);
    }
    if (res.status === 204) return [];
    return (await res.json().catch(() => [])) as T[];
  }

  /**
   * Resolve the active condo id from persisted auth state without importing
   * the auth store (api.ts <- authStore would be a circular import). Mirrors
   * the `auth-storage` Zustand persist key that httpClient.ts already reads.
   */
  protected resolveActiveCondoId(): string | undefined {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return undefined;
      const persisted = window.localStorage.getItem('auth-storage');
      if (!persisted) return undefined;
      const parsed = JSON.parse(persisted);
      return parsed?.state?.currentCondo?.id || undefined;
    } catch {
      return undefined;
    }
  }
}
